import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useSalesforceOrg } from '@/api/hooks/use-salesforce';
import { useUpdateSalesforceOrg } from '@/features/salesforce/hooks';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { HubCard, FieldHint } from '@/shared/components/HubLayout';
import { PageSkeleton } from '@/shared/components/PageSkeleton';
import { ApiError } from '@/api/errors';
import type { UUID } from '@/types';

export type SalesforceOrgEditFormProps = {
  orgId: UUID;
  onSaved?: () => void;
  onCancel?: () => void;
};

/** Edit registered Salesforce org metadata including the 18-char org ID. */
export function SalesforceOrgEditForm({ orgId, onSaved, onCancel }: SalesforceOrgEditFormProps) {
  const { data: org, isLoading } = useSalesforceOrg(orgId);
  const updateOrg = useUpdateSalesforceOrg(orgId);

  const [salesforceOrgId, setSalesforceOrgId] = useState('');
  const [instanceUrl, setInstanceUrl] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [scrtUrl, setScrtUrl] = useState('');
  const [scrtProvider, setScrtProvider] = useState('');
  const [environment, setEnvironment] = useState<'production' | 'sandbox'>('production');

  useEffect(() => {
    if (org) {
      setSalesforceOrgId(org.salesforce_org_id ?? '');
      setInstanceUrl(org.instance_url ?? '');
      setDisplayName(org.display_name ?? '');
      setScrtUrl(org.scrt_url ?? '');
      setScrtProvider(org.scrt_telephony_provider_name ?? '');
      setEnvironment(org.environment ?? 'production');
    }
  }, [org]);

  if (isLoading || !org) return <PageSkeleton variant="card" />;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!salesforceOrgId.trim()) {
      toast.error('Salesforce Org ID is required');
      return;
    }
    try {
      await updateOrg.mutateAsync({
        salesforce_org_id: salesforceOrgId.trim(),
        instance_url: instanceUrl.trim() || undefined,
        display_name: displayName.trim(),
        environment,
        scrt_url: scrtUrl.trim() || null,
        scrt_telephony_provider_name: scrtProvider.trim() || null,
      });
      toast.success('Organization updated');
      onSaved?.();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Failed to update org');
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <HubCard className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="editSalesforceOrgId">Salesforce Org ID *</Label>
          <Input
            id="editSalesforceOrgId"
            value={salesforceOrgId}
            onChange={(e) => setSalesforceOrgId(e.target.value)}
            placeholder="00D000000000000EAA"
            className="font-mono text-sm"
            required
          />
          <FieldHint>18-character ID from Salesforce Setup → Company Information.</FieldHint>
        </div>
        <div className="space-y-2">
          <Label htmlFor="editDisplayName">Display name *</Label>
          <Input
            id="editDisplayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="editScrtUrl">SCRT URL</Label>
          <Input
            id="editScrtUrl"
            value={scrtUrl}
            onChange={(e) => setScrtUrl(e.target.value)}
            placeholder="https://your-org.my.salesforce-scrt.com"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="editScrtProvider">SCRT telephony provider</Label>
          <Input
            id="editScrtProvider"
            value={scrtProvider}
            onChange={(e) => setScrtProvider(e.target.value)}
            placeholder="Twilio"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="editInstanceUrl">Instance URL (REST)</Label>
          <Input
            id="editInstanceUrl"
            value={instanceUrl}
            onChange={(e) => setInstanceUrl(e.target.value)}
            placeholder="https://yourorg.my.salesforce.com"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="editEnvironment">Environment</Label>
          <select
            id="editEnvironment"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={environment}
            onChange={(e) => setEnvironment(e.target.value as 'production' | 'sandbox')}
          >
            <option value="production">Production</option>
            <option value="sandbox">Sandbox</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button type="submit" disabled={updateOrg.isPending}>
            {updateOrg.isPending ? 'Saving…' : 'Save'}
          </Button>
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      </HubCard>
    </form>
  );
}

export function isValidSalesforceOrgId(value: string) {
  const trimmed = value.trim();
  return /^00D[a-zA-Z0-9]{12}(?:[a-zA-Z0-9]{3})?$/.test(trimmed);
}

export function isPlaceholderSalesforceOrgId(value: string) {
  const trimmed = value.trim().toLowerCase();
  return !trimmed || trimmed === 'dummy' || trimmed === 'test' || !isValidSalesforceOrgId(value);
}
